import React, { type ComponentType } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconProps } from '@lds-efsp/shared-ui';

type CardVariant = 'primary' | 'secondary';
type CardSize = 'default' | 'large';

interface DashboardCardProps {
  icon: ComponentType<IconProps>;
  label: string;
  count?: number;
  alerts?: number;
  onClick?: () => void;
  variant?: CardVariant;
  compact?: boolean;
  size?: CardSize;
  brand: BrandConfig;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  icon: Icon,
  label,
  count,
  alerts = 0,
  onClick,
  variant = 'primary',
  compact = false,
  size = 'default',
  brand
}) => {
  const isPrimary = variant === 'primary';
  const isLarge = size === 'large';

  // Determine padding and icon size based on compact and size props
  const getPaddingClass = (): string => {
    if (compact) return 'px-4 py-3';
    if (isLarge) return 'px-8 py-6';
    return 'px-5 py-4';
  };

  const getIconContainerClass = (): string => {
    if (compact) return 'w-10 h-10';
    if (isLarge) return 'w-16 h-16';
    return 'w-12 h-12';
  };

  const getIconClass = (): string => {
    if (compact) return 'w-5 h-5';
    if (isLarge) return 'w-8 h-8';
    return 'w-6 h-6';
  };

  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-3 rounded-xl shadow-xs transition-all
        hover:shadow-md hover:scale-[1.02]
        ${getPaddingClass()}
      `}
      style={{
        backgroundColor: isPrimary ? brand.colors.primaryLight : 'white',
        border: `1px solid ${brand.colors.border}`,
      }}
    >
      <div
        className={`
          flex items-center justify-center rounded-lg
          ${getIconContainerClass()}
        `}
        style={{
          backgroundColor: isPrimary ? brand.colors.primary : brand.colors.primaryLight,
          color: isPrimary ? brand.colors.onPrimary : brand.colors.link,
        }}
      >
        <Icon className={getIconClass()} />
      </div>

      <div className="flex flex-col items-start">
        <span
          className={`font-medium ${compact ? 'text-sm' : 'text-base'}`}
          style={{ color: brand.colors.secondary }}
        >
          {label}
          {count !== undefined && (
            <span className="ml-1" style={{ color: brand.colors.link }}>
              ({count})
            </span>
          )}
        </span>
      </div>

      {alerts > 0 && (
        <div
          className="ml-auto flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white"
          style={{ backgroundColor: brand.colors.error }}
        >
          {alerts}
        </div>
      )}
    </button>
  );
};

export default DashboardCard;
